/**
 * NotificationCenter — Header-Glocke
 *
 * Cluster D-notifications (Finding 7): Die Glocke las früher ausschließlich aus
 * dem lokalen (localStorage-persistierten) notificationStore und war daher in
 * frischen Sessions immer leer, während /notifications dutzende Server-Einträge
 * zeigte. Primärquelle sind jetzt die Server-Notifications
 * (GET /api/v2/notifications via useNotifications) — der lokale Store bleibt
 * Quelle für Live-SSE-Meldungen und wird eingemischt.
 *
 * Bewusst KEIN eigenes Polling-Intervall: der Hook nutzt staleTime 30s, das
 * Dropdown hängt im Shell-Layout auf jeder Seite.
 */
import React, { useState, useRef, useEffect, useMemo } from 'react'
import { Bell, Check, Trash2, Info, AlertTriangle, XCircle, CheckCircle2 } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { Link } from '@tanstack/react-router'
import { activeDateFnsLocale } from '../lib/locale'
import { useTranslation } from 'react-i18next'
import { useNotificationStore, type NotificationSeverity } from '../stores/notificationStore'
import {
  useNotifications,
  useMarkAllRead,
  type Notification as ServerNotification,
} from '../features/notifications/useNotifications'
import { notificationBadgeConfig } from '../features/notifications/notificationBadge'
import { normalizeNotificationMessage } from '../features/notifications/normalizeMessage'

const SEVERITY_ICONS: Record<NotificationSeverity, React.ElementType> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  error: XCircle,
}

const SEVERITY_COLORS: Record<NotificationSeverity, string> = {
  info: 'var(--primary)',
  success: 'var(--success)',
  warning: 'var(--warning)',
  error: 'var(--danger)',
}

/** Maximale Anzahl Einträge im Dropdown (Server + lokal gemerged). */
const MAX_DROPDOWN_ITEMS = 50
/** Bodies im Dropdown sind kürzer als auf /notifications. */
const DROPDOWN_MESSAGE_LENGTH = 120

type DropdownItem = {
  key: string
  source: 'server' | 'local'
  localId?: string
  title: string
  body?: string
  timestamp: number
  read: boolean
  icon: React.ElementType
  color: string
  onClick?: () => void
}

function serverToItem(n: ServerNotification): DropdownItem {
  const cfg = notificationBadgeConfig(n.event_type)
  const message = normalizeNotificationMessage(n.message, DROPDOWN_MESSAGE_LENGTH)

  return {
    key: `server-${n.id}`,
    source: 'server',
    title: n.title || message,
    body: n.title ? message : undefined,
    timestamp: new Date(n.created_at).getTime(),
    read: n.read_at !== null,
    icon: cfg.icon,
    color: 'var(--primary)',
  }
}

export const NotificationCenter = React.memo(function NotificationCenter() {
  const { t } = useTranslation('components')
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const localNotifications = useNotificationStore((state) => state.notifications)
  const markRead = useNotificationStore((state) => state.markRead)
  const markAllReadLocal = useNotificationStore((state) => state.markAllRead)
  const clearLocal = useNotificationStore((state) => state.clear)

  const { data: response } = useNotifications('all')
  const markAllRead = useMarkAllRead()

  const serverNotifications: ServerNotification[] | undefined = response?.data

  // Vollstaendige Merge-Liste (ungeslict) — Basis fuer den Ungelesen-Zaehler.
  const allNotifications = useMemo<DropdownItem[]>(() => {
    const serverItems = (serverNotifications ?? []).map(serverToItem)
    const localItems: DropdownItem[] = localNotifications.map((n) => ({
      key: `local-${n.id}`,
      source: 'local',
      localId: n.id,
      title: n.title,
      body: n.body,
      timestamp: n.timestamp,
      read: n.read,
      icon: SEVERITY_ICONS[n.severity],
      color: SEVERITY_COLORS[n.severity],
      onClick: n.onClick,
    }))

    return [...localItems, ...serverItems].sort((a, b) => b.timestamp - a.timestamp)
  }, [serverNotifications, localNotifications])

  // Anzeige-Liste ist auf MAX_DROPDOWN_ITEMS begrenzt, der Zaehler bewusst NICHT:
  // sonst koennen viele lokale SSE-Items alle Server-Items verdraengen.
  const notifications = useMemo<DropdownItem[]>(
    () => allNotifications.slice(0, MAX_DROPDOWN_ITEMS),
    [allNotifications],
  )

  // Ungelesen-Zähler kommt aus beiden Quellen (Server: read_at === null).
  const unreadCount = allNotifications.filter((n) => !n.read).length
  const hasLocalNotifications = localNotifications.length > 0

  function handleMarkAllRead() {
    markAllReadLocal()
    markAllRead.mutate()
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative' }}
      className="notification-center-container"
    >
      <button
        className="btn btn-icon btn-ghost"
        aria-label={t('notificationCenter.triggerAriaLabel')}
        aria-expanded={isOpen}
        data-testid="notifications-button"
        onClick={() => setIsOpen(!isOpen)}
        style={{ position: 'relative' }}
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span
            data-testid="notifications-badge"
            style={{
              position: 'absolute',
              top: 6,
              right: 6,
              width: 8,
              height: 8,
              borderRadius: '999px',
              background: 'var(--danger)',
              display: 'block',
            }}
            aria-hidden="true"
          />
        )}
      </button>

      {isOpen && (
        <div
          data-testid="notifications-dropdown"
          className="notification-dropdown"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: '350px',
            maxHeight: '400px',
            backgroundColor: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            boxShadow: 'var(--shadow-lg)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 100,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: 'var(--surface-sunken)',
            }}
          >
            <span style={{ fontWeight: 600, fontSize: '14px' }}>{t('notificationCenter.heading')}</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                className="btn btn-ghost"
                style={{ fontSize: '12px', padding: '4px 8px', height: 'auto' }}
                onClick={handleMarkAllRead}
                disabled={unreadCount === 0 || markAllRead.isPending}
                aria-label={t('notificationCenter.markAllReadAriaLabel')}
              >
                <Check size={14} style={{ marginRight: '4px' }}/> {t('notificationCenter.markAllRead')}
              </button>
              <button
                className="btn btn-ghost"
                style={{ fontSize: '12px', padding: '4px 8px', height: 'auto', color: 'var(--danger)' }}
                onClick={clearLocal}
                disabled={!hasLocalNotifications}
                data-testid="notifications-clear-local"
                aria-label={t('notificationCenter.clearLocalAriaLabel')}
              >
                <Trash2 size={14} style={{ marginRight: '4px' }}/> {t('notificationCenter.clearLocal')}
              </button>
            </div>
          </div>

          {/* List */}
          <div
            aria-live="polite"
            style={{
              overflowY: 'auto',
              flex: 1,
            }}
          >
            {notifications.length === 0 ? (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--text-subtle)', fontSize: '14px' }}>
                {t('notificationCenter.empty')}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {notifications.map((notification) => {
                  const Icon = notification.icon
                  const color = notification.color

                  return (
                    <div
                      key={notification.key}
                      onClick={() => {
                        if (notification.localId) {
                          markRead(notification.localId)
                        }
                        if (notification.onClick) {
                          notification.onClick()
                          setIsOpen(false)
                        }
                      }}
                      style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid var(--border)',
                        backgroundColor: notification.read ? 'transparent' : 'var(--surface-sunken)',
                        cursor: 'pointer',
                        display: 'flex',
                        gap: '12px',
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <div style={{ color, marginTop: '2px' }}>
                        <Icon size={16} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                          <span style={{ fontWeight: notification.read ? 500 : 600, fontSize: '14px', color: 'var(--text)' }}>
                            {notification.title}
                          </span>
                          <span style={{ fontSize: '11px', color: 'var(--text-subtle)', whiteSpace: 'nowrap', marginLeft: '8px' }}>
                            {formatDistanceToNow(notification.timestamp, { addSuffix: true, locale: activeDateFnsLocale() })}
                          </span>
                        </div>
                        {notification.body && (
                          <div style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                            {notification.body}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '10px 16px',
              borderTop: '1px solid var(--border)',
              textAlign: 'center',
              backgroundColor: 'var(--surface-sunken)',
            }}
          >
            <Link
              to="/notifications"
              data-testid="notifications-view-all"
              onClick={() => setIsOpen(false)}
              style={{ fontSize: '12px', fontWeight: 600 }}
            >
              {t('notificationCenter.viewAll')}
            </Link>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 639px) {
          .notification-dropdown {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            width: 100% !important;
            max-height: 100vh !important;
            border-radius: 0 !important;
            z-index: 9999 !important;
          }
          /* Add a close button for mobile if needed, or rely on the header being clickable/esc */
          .notification-dropdown > div:first-child {
            padding-top: max(12px, env(safe-area-inset-top)) !important;
          }
        }
      `}</style>
    </div>
  )
})
